import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { EQUIPMENT_SENSOR_RESOURCE_LINK } from '~/client/src/shared/resources/resourceLink/managedResourceLinks'

import { OWNER_FORM_DEFAULTS } from './ownerFormDefaults'
import {
  getSensorStateLabel,
  resolveEquipmentSensorState,
  resolveSensorStateStatusBadge,
} from './sensorState'
import { IResourceDefinition, IResourceFieldDefinition } from './types'

// ID, Icon and Name lead the table and stay frozen; the rest follow in this
// order and are the only columns visible by default.
const EQUIPMENT_FROZEN_COLUMNS = ['stxResourceId', 'iconName', 'name']
const EQUIPMENT_DEFAULT_COLUMNS = [
  ...EQUIPMENT_FROZEN_COLUMNS,
  'sensor_state',
  'file_or_photo',
  'type',
  'company',
  'responsible',
  'workflow_count',
  'total_operational_hours',
]

// Pre-selected on a new Equipment form. Name, Icon, Color and Parent are system
// fields the editor always includes, so they aren't listed here.
const EQUIPMENT_DEFAULT_FORM_PROPERTIES = [
  'file_or_photo',
  'type',
  'company',
  'responsible',
]

const EQUIPMENT_FIELD_DEFINITIONS: Record<string, IResourceFieldDefinition> = {
  type: {
    dataType: 'text',
    card: { kind: 'text', visible: true },
    table: {
      sortable: true,
      groupable: true,
      filterable: true,
      searchable: true,
      labelKey: 'type',
    },
  },
  manufacturer: {
    dataType: 'text',
    card: { kind: 'text', visible: true },
    table: {
      sortable: true,
      groupable: true,
      filterable: true,
      searchable: true,
      labelKey: 'manufacturer',
    },
  },
  fuel_type: {
    dataType: 'text',
    card: { kind: 'text', visible: true },
    table: {
      sortable: true,
      groupable: true,
      filterable: true,
      searchable: true,
      labelKey: 'fuel_type',
    },
  },
  // No `table` block: card presentation only, so the property's own type keeps deciding
  // what it can do in a list.
  company: {
    dataType: 'text',
    // The company's logo when it has one; iconKey's building glyph when it does not.
    card: {
      kind: 'link',
      iconKey: 'company',
      iconKind: 'companyLogo',
      visible: true,
    },
  },
  responsible: {
    dataType: 'text',
    // The person's own avatar per the card design; iconKey's glyph when the member
    // cannot be resolved, so the icon box is never left empty.
    card: {
      kind: 'link',
      iconKey: 'user',
      iconKind: 'avatar',
      visible: true,
    },
  },
  sensor_state: {
    dataType: 'text',
    format: ['sensorStateLabel'],
    card: {
      isStatus: true,
      resolveStatusBadge: resolveSensorStateStatusBadge,
      visible: true,
    },
    table: {
      groupable: true,
      filterable: true,
      searchable: true,
      labelKey: 'struxTagState',
      semanticLabelFn: value => getSensorStateLabel(value),
    },
    edit: { readOnly: true },
  },
  sensor_name: {
    dataType: 'text',
    card: {
      kind: 'link',
      iconKey: 'sensor',
      visible: true,
    },
    table: { searchable: true },
    edit: { readOnly: true },
  },
  sensor_battery: {
    dataType: 'number',
    format: ['round', 'percent'],
    card: {
      kind: 'metric',
      iconKey: 'battery',
      visible: true,
    },
    table: { searchable: true },
    edit: { readOnly: true },
  },
  latitude: {
    dataType: 'number',
    card: { visible: false },
    table: {},
    edit: { readOnly: true },
  },
  longitude: {
    dataType: 'number',
    card: { visible: false },
    table: {},
    edit: { readOnly: true },
  },
  // Equipment root fields (edited in the resource form) exposed to the table.
  // iconName/color are display-only columns — glyph + swatch, not sortable/
  // groupable/filterable. parent is a location reference resolving to its
  // parent's name, and supports group/sort/filter via the location rail.
  iconName: {
    dataType: 'text',
    card: { visible: false },
    table: { column: true, cellType: 'icon', labelKey: 'icon' },
  },
  color: {
    dataType: 'text',
    card: { visible: false },
    table: { column: true, cellType: 'color', labelKey: 'color' },
  },
  parent: {
    dataType: 'location',
    card: { visible: false },
    table: {
      sortable: true,
      groupable: true,
      filterable: true,
      // Matches the card's label: the column, filter, group-by and sort all read this.
      labelKey: 'lbsParent',
    },
  },
}

export const equipmentResourceDefinition: IResourceDefinition = {
  resourceTypeId: ResourceTypeId.OFFLOADING_EQUIPMENT,
  fields: EQUIPMENT_FIELD_DEFINITIONS,
  valueSources: {
    stxResourceId: { source: 'root' },
    sensor_state: {
      source: 'root',
      resolve: resolveEquipmentSensorState,
    },
    sensor_name: {
      source: 'root',
      path: 'linkedSensor.name',
    },
    sensor_battery: {
      source: 'root',
      path: 'linkedSensor.data.battery',
    },
    [EQUIPMENT_SENSOR_RESOURCE_LINK.propertyName]: {
      source: 'root',
      path: 'linkedSensor.name',
    },
    latitude: {
      source: 'root',
      path: 'effectivePosition.latitude',
    },
    longitude: {
      source: 'root',
      path: 'effectivePosition.longitude',
    },
    // iconName/color/parent are top-level record props (mapRecord puts them there,
    // not in root/data), so read them off the record directly. `parent` projects
    // to its parentId so the location rail can resolve the parent's name.
    iconName: {
      source: 'root',
      resolve: record => record.iconName ?? null,
    },
    color: {
      source: 'root',
      resolve: record => record.color ?? null,
    },
    parent: {
      source: 'root',
      resolve: record => record.parent?.parentId ?? null,
    },
  },
  card: {
    titleKey: 'name',
    subtitleKey: 'stxResourceId',
    // The card's meta rows, in order. sensor_state is absent on purpose: it drives the
    // status badge via resolveStatusBadge, not a meta row.
    defaultDisplayPropertyKeys: [
      'company',
      'responsible',
      'sensor_name',
      'sensor_battery',
    ],
  },
  map: {
    latitudeKey: 'latitude',
    longitudeKey: 'longitude',
  },
  table: {
    columnOrder: EQUIPMENT_DEFAULT_COLUMNS,
    defaultVisibleKeys: EQUIPMENT_DEFAULT_COLUMNS,
    defaultFrozenKeys: EQUIPMENT_FROZEN_COLUMNS,
    // A linked StruxTag with no telemetry yet resolves to no state, so keying off
    // the state itself keeps the column out until there is something to read.
    defaultVisibleWhenPopulatedKeys: ['sensor_state'],
  },
  form: {
    defaultSelectedKeys: EQUIPMENT_DEFAULT_FORM_PROPERTIES,
    // Parent comes from the location an equipment is drawn inside, so it is only
    // worth asking for in Maps.
    mapsOnlyRootFields: ['parent'],
    ...OWNER_FORM_DEFAULTS,
  },
  ui: {
    noneGroupingKey: 'none',
    defaultGroupingKey: null,
    defaultSortKey: 'name',
    defaultSortOrder: SortOrder.ASC,
  },
}
